import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Tooltip, Typography } from '@saviynt/design-system';

import BarGroupRenderer from './BarGroupRenderer';
import {
  DEFAULT_ANIMATION_DURATION,
  DEFAULT_FIRST_SEGMENT_COLOR,
  DEFAULT_HEIGHT,
  DEFAULT_LAST_SEGMENT_COLOR,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SEGMENT_COLORS,
  DEFAULT_WIDTH,
  GEOMETRY_RATIOS,
  NO_DATA_TEXT,
} from './constants';
import type { CategoryConfig, StackedBarChartProps } from './types';
import {
  calculateChartDimensions,
  calculateSegments,
  calculateYScale,
  extractCategoriesFromData,
  prefersReducedMotion,
} from './utils';
import XAxisRenderer from './XAxisRenderer';
import YAxisRenderer from './YAxisRenderer';

import './StackedBarChart.css';

function StackedBarChart({
  data,
  overlayMetric,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  animationDuration = DEFAULT_ANIMATION_DURATION,
  xAxisConfig,
  currentPage: controlledCurrentPage,
  pageSize = DEFAULT_PAGE_SIZE,
  onPageChange,
  onBarClick,
  onSegmentHover,
  showLegend,
  segmentColors,
  segmentGradients,
  'aria-label': ariaLabel,
  className,
}: StackedBarChartProps): React.ReactElement {
  const [uncontrolledCurrentPage, setUncontrolledCurrentPage] = useState(0);
  const currentPage = controlledCurrentPage ?? uncontrolledCurrentPage;

  const handlePageChange = (newPage: number) => {
    if (controlledCurrentPage === undefined) {
      setUncontrolledCurrentPage(newPage);
    }

    onPageChange?.(newPage);
  };

  const [animationProgress, setAnimationProgress] = useState(0);
  const frameIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<null | number>(null);

  const validatedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data.filter((item) => {
      if (!Array.isArray(item.segments)) {
        return false;
      }

      return true;
    });
  }, [data]);

  const categoriesMap = useMemo(() => {
    const map = new Map<string, CategoryConfig>();
    const generatedCategories = extractCategoriesFromData(
      validatedData,
      segmentColors
    );

    generatedCategories.forEach((category) => {
      if (typeof category.id === 'string') {
        map.set(category.id, category);
      }
    });

    return map;
  }, [validatedData, segmentColors]);

  const dimensions = useMemo(() => {
    try {
      return calculateChartDimensions(width, height);
    } catch (error) {
      return calculateChartDimensions(DEFAULT_WIDTH, DEFAULT_HEIGHT);
    }
  }, [width, height]);

  const yScaleResult = useMemo(() => {
    if (validatedData.length === 0) {
      return calculateYScale(0, dimensions.chartHeight);
    }

    let maxTotal = 0;

    validatedData.forEach((item) => {
      const barTotal = item.segments.reduce((acc, seg) => acc + seg.value, 0);

      maxTotal = Math.max(maxTotal, barTotal);
    });

    return calculateYScale(maxTotal, dimensions.chartHeight);
  }, [validatedData, dimensions.chartHeight]);

  const barGeometry = useMemo(() => {
    if (validatedData.length === 0) {
      return {
        barWidth: 0,
        barSpacing: 0,
        totalBarsWidth: 0,
      };
    }

    const barSpacing = dimensions.chartWidth / validatedData.length;
    const barWidth = barSpacing * GEOMETRY_RATIOS.barWidthRatio;
    const totalBarsWidth = barSpacing * validatedData.length;

    return {
      barWidth,
      barSpacing,
      totalBarsWidth,
    };
  }, [validatedData.length, dimensions.chartWidth]);

  const paginatedData = useMemo(() => {
    const startIdx = currentPage * pageSize;
    const endIdx = startIdx + pageSize;

    return validatedData.slice(startIdx, endIdx);
  }, [currentPage, pageSize, validatedData]);

  useEffect(() => {
    if (prefersReducedMotion() || validatedData.length === 0) {
      setAnimationProgress(1);

      return undefined;
    }

    setAnimationProgress(0);
    startTimeRef.current = null;

    const animate = (currentTime: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / animationDuration, 1);

      setAnimationProgress(progress);

      if (progress < 1) {
        frameIdRef.current = requestAnimationFrame(animate);
      }
    };

    frameIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, [validatedData.length, animationDuration]);

  const segmentsByBar = useMemo(
    () =>
      paginatedData.map((item) => {
        const segments = calculateSegments(
          item.segments,
          categoriesMap,
          yScaleResult.scale,
          segmentColors
        );

        return {
          segments,
          label: item.label,
          overlayValue: item.overlayValue,
          barIndex: validatedData.indexOf(item),
        };
      }),
    [
      paginatedData,
      categoriesMap,
      yScaleResult.scale,
      validatedData,
      segmentColors,
    ]
  );

  if (validatedData.length === 0) {
    return (
      <div className={`StackedBarChart ${className ?? ''}`}>
        <Typography kind='body1'>{NO_DATA_TEXT}</Typography>
      </div>
    );
  }

  const totalPages = Math.ceil(validatedData.length / pageSize);

  const generateTooltipText = () => {
    if (hoveredBarIndex === null || !segmentsByBar[hoveredBarIndex]) {
      return '';
    }

    const barData = segmentsByBar[hoveredBarIndex];
    const lines: string[] = [];

    barData.segments.forEach((seg) => {
      const category = categoriesMap.get(seg.categoryId);

      lines.push(
        `${category?.label ?? seg.categoryId}: ${seg.value.toLocaleString()}`
      );
    });

    if (barData.overlayValue !== undefined && overlayMetric?.formatter) {
      lines.push(
        `${overlayMetric.label}: ${overlayMetric.formatter(
          barData.overlayValue
        )}`
      );
    }

    return lines.join('\n');
  };

  return (
    <div className={`StackedBarChart ${className ?? ''}`}>
      {showLegend && (
        <div className='StackedBarChart-legend'>
          {Array.from(categoriesMap.values()).map((category, index) => {
            const isFirstSegment = index === 0;
            const isLastSegment = index === categoriesMap.size - 1;

            let color: string;

            if (segmentColors?.[index]) {
              color = segmentColors[index];
            } else if (isFirstSegment) {
              color = DEFAULT_FIRST_SEGMENT_COLOR;
            } else if (isLastSegment) {
              color = DEFAULT_LAST_SEGMENT_COLOR;
            } else if (category.color) {
              color = category.color;
            } else {
              color =
                DEFAULT_SEGMENT_COLORS[index % DEFAULT_SEGMENT_COLORS.length];
            }

            return (
              <div key={category.id} className='StackedBarChart-legendItem'>
                <span
                  className='StackedBarChart-legendColor'
                  style={{ backgroundColor: color }}
                />
                <Typography kind='body2'>{category.label}</Typography>
              </div>
            );
          })}
        </div>
      )}

      <svg
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio='xMidYMid meet'
        role='img'
        aria-label={ariaLabel}
        className='StackedBarChart-svg'>
        <YAxisRenderer
          yScaleResult={yScaleResult}
          dimensions={dimensions}
          className='StackedBarChart-yAxis'
        />

        <XAxisRenderer
          data={paginatedData}
          chartBottom={dimensions.chartBottom}
          chartLeft={dimensions.chartLeft}
          chartRight={dimensions.chartRight}
          barSpacing={barGeometry.barSpacing}
          xAxisConfig={xAxisConfig}
          dimensions={{
            chartLeft: dimensions.chartLeft,
            chartBottom: dimensions.chartBottom,
          }}
          barGeometry={{
            barSpacing: barGeometry.barSpacing,
          }}
        />

        <g className='StackedBarChart-bars' role='presentation'>
          {segmentsByBar.map((barData, index) => {
            const slotX = dimensions.chartLeft + index * barGeometry.barSpacing;
            const barIndexInData = currentPage * pageSize + index;

            return (
              <foreignObject
                key={`tooltip-${barData.label}`}
                x={slotX}
                y={dimensions.chartTop}
                width={barGeometry.barSpacing}
                height={dimensions.chartHeight}
                style={{ overflow: 'visible' }}>
                <Tooltip
                  trigger={
                    <div className='StackedBar-Tooltip'>
                      <svg
                        width='100%'
                        height='100%'
                        style={{ overflow: 'visible' }}>
                        <BarGroupRenderer
                          segments={barData.segments}
                          slotX={0}
                          slotWidth={barGeometry.barSpacing}
                          barWidth={barGeometry.barWidth}
                          chartTop={0}
                          chartBottom={dimensions.chartHeight}
                          chartHeight={dimensions.chartHeight}
                          animationProgress={animationProgress}
                          isHovered={hoveredBarIndex === index}
                          barIndex={barIndexInData}
                          label={barData.label}
                          overlayValue={barData.overlayValue}
                          overlayFormatter={overlayMetric?.formatter}
                          overlayColor={overlayMetric?.color}
                          segmentGradients={segmentGradients}
                          onMouseEnter={() => setHoveredBarIndex(index)}
                          onMouseLeave={() => setHoveredBarIndex(null)}
                          onClick={() => {
                            const total = barData.segments.reduce(
                              (sum, seg) => sum + seg.value,
                              0
                            );

                            onBarClick?.({
                              barIndex: barIndexInData,
                              label: barData.label,
                              total,
                              segments: barData.segments,
                            });
                          }}
                          onSegmentHover={(segmentId, isHovered) => {
                            const segment = barData.segments.find(
                              (seg) => seg.categoryId === segmentId
                            );

                            if (segment) {
                              onSegmentHover?.({
                                barIndex: barIndexInData,
                                label: barData.label,
                                segment,
                                isHovered,
                              });
                            }
                          }}
                        />
                      </svg>
                    </div>
                  }
                  text={hoveredBarIndex === index ? generateTooltipText() : ''}
                  placement='top'
                  isPortalEnabled
                />
              </foreignObject>
            );
          })}
        </g>
      </svg>

      {totalPages > 1 && (
        <div className='StackedBarChart-pagination'>
          <button
            type='button'
            className='StackedBarChart-paginationButton'
            onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            aria-label='Previous page'>
            <Icon kind='ChevronLeft' size='small' color='white' />
          </button>

          <div className='StackedBarChart-paginationDots'>
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                // eslint-disable-next-line react/no-array-index-key
                key={`dot-${index}`}
                type='button'
                className={`StackedBarChart-paginationDot ${
                  index === currentPage ? 'active' : ''
                }`}
                onClick={() => handlePageChange(index)}
                aria-label={`Go to page ${index + 1}`}
                aria-current={index === currentPage ? 'page' : undefined}
              />
            ))}
          </div>

          <button
            type='button'
            className='StackedBarChart-paginationButton'
            onClick={() =>
              handlePageChange(Math.min(totalPages - 1, currentPage + 1))
            }
            disabled={currentPage >= totalPages - 1}
            aria-label='Next page'>
            <Icon kind='ChevronRight' size='small' color='white' />
          </button>
        </div>
      )}
    </div>
  );
}

export default React.memo(StackedBarChart);
